/**
 * Interactive Site Map Drawer Plugin (mobile only)
 *
 * Mounts `[data-panel]` into the drawer starting in a collapsed (72 px peek) state.
 * Tap expands to full open; swipe-down snaps back to collapsed — both behaviours are
 * owned by `drawer.js` via the `peek` option passed to `mountElement`.
 *
 * Also subscribes to `selectDrawerMode` (derived from `activeView`) and calls
 * `drawerService.expandToFullSize()` / `collapseToPartial()` accordingly — see
 * DRAWER_PLUGIN_SEQUENCE.md. The plugin never decides its own size; it only reflects the selector.
 *
 * On top of that size, it drives the collapsed/expanded state from map interaction: selecting a
 * lot expands the sheet (`expandPanel`), and a background tap that doesn't unwind a sub-view
 * collapses it back to the peek strip (`collapsePanel`).
 *
 * `restorePanel` is registered so the panel node is moved back to its original parent
 * before `drawerContainer.innerHTML` is cleared, preventing the node from being destroyed.
 *
 * When to touch this file:
 *  - Any new drawer lifecycle hooks (chip changes, view transitions, etc.).
 *  - If `drawerService` API changes (mountElement, onHtmlRemoved, clearHtml, expandToFullSize,
 *    collapseToPartial, expandPanel, collapsePanel).
 *
 * Mobile-only is enforced by gating on `context.state.isMobile`
 */
import { distinctUntilChanged, filter, map } from 'rxjs/operators';
import DrawerMode from 'layout/enums/drawerMode';
import { selectDrawerMode, selectSelectedLotId, selectViewHistory } from '../redux/selectors';

export const DRAWER_ID = 'native-interactive-site-map-drawer';
const PEEK_HEIGHT = '54px';

export default function createInteractiveSiteMapDrawerPlugin() {
  const state = {
    mounted: false,
    subscriptions: [],
  };

  return {
    name: DRAWER_ID,

    async start(context) {
      if (!context.state.isMobile) {
        return;
      }

      const { drawerService, store$ } = context.services;
      if (!drawerService) {
        return;
      }

      const panelElement = context.root.querySelector('[data-panel]');
      if (!panelElement) {
        return;
      }

      const originalParent = panelElement.parentElement;

      function restorePanel() {
        if (panelElement.parentElement !== originalParent) {
          originalParent.appendChild(panelElement);
        }
      }

      function onDrawerModeChanged(mode) {
        if (mode === DrawerMode.Hidden) {
          drawerService.hideDrawer();
          return;
        }

        drawerService.showDrawer();

        if (mode === DrawerMode.Full) {
          drawerService.expandToFullSize();
        } else {
          drawerService.collapseToPartial();
        }
      }

      // Selecting a lot on the map fills the panel with that lot's content, so the sheet lifts out
      // of its peek strip to show it. Hidden views (mobile lot card) own the map on their own and
      // must stay untouched — expanding there un-hides the drawer over them.
      function onLotSelected() {
        if (selectDrawerMode(context.services.getState()) === DrawerMode.Hidden) {
          return;
        }

        drawerService.expandPanel();
      }

      // A background tap that has a sub-view to unwind is a "go back", not a "dismiss" — only the
      // taps mapBackgroundClickedEpic resolves without navigating collapse the sheet.
      function onMapBackgroundClicked() {
        const currentState = context.services.getState();
        if (
          selectDrawerMode(currentState) === DrawerMode.Hidden ||
          selectViewHistory(currentState).length > 0
        ) {
          return;
        }

        drawerService.collapsePanel();
      }

      // Register restorePanel BEFORE mountElement to guarantee it is in place
      // before the drawer can be closed. Race: clearHtml could be dispatched
      // between mountElement and onHtmlRemoved registration if reversed.
      drawerService.onHtmlRemoved({ name: DRAWER_ID, callback: restorePanel });
      drawerService.mountElement(panelElement, { peek: PEEK_HEIGHT });
      state.mounted = true;

      state.subscriptions.push(
        store$.pipe(
          map(selectDrawerMode),
          distinctUntilChanged(),
        ).subscribe(onDrawerModeChanged),
      );

      // Subscribed after the drawer mode above so that on a store emission carrying both a new lot
      // and a new view, the view's size is applied first and this only tops it up.
      state.subscriptions.push(
        store$.pipe(
          map(selectSelectedLotId),
          distinctUntilChanged(),
          filter(Boolean),
        ).subscribe(onLotSelected),
      );

      state.subscriptions.push(
        context.services.mapBackgroundClicked$?.subscribe(onMapBackgroundClicked),
      );
    },

    async stop(context) {
      state.subscriptions.forEach((subscription) => subscription?.unsubscribe?.());
      state.subscriptions = [];

      if (!state.mounted) {
        return;
      }

      context.services.drawerService?.showDrawer?.();

      context.services.drawerService?.clearHtml?.();
      state.mounted = false;
    },
  };
}
